/**
 * The US EPA AQI band model both apps draw air quality with: thresholds, the
 * band word and health sentence, and the band palette. Moved from mobile's
 * `screens/common/AirQuality/utils.ts` (FE #331) so the webapp reads the same
 * bands; mobile re-exports it.
 *
 * The hex values here are a depicting palette (the EPA's own band colours,
 * read as data), the one kind of colour allowed to cross into core.
 */
import {messages} from '@smartmixin/i18n';

const GOOD = 50;
const MODERATE = 100;
const SENSITIVE_UNHEALTHY = 150;
const UNHEALTHY = 200;
const VERY_UNEAHTLY = 300;
const HAZARDOUS = 500;

export type AqiBand = {
  key:
    | 'good'
    | 'moderate'
    | 'sensitive'
    | 'unhealthy'
    | 'veryUnhealthy'
    | 'hazardous';
  low: number;
  high: number;
  /** The vivid hue. Only ever used as a fill — see `ink` / `on` for text. */
  fill: string;
  /** Foreground on the solid fill. */
  ink: string;
  /** Foreground on a tint of the fill, or on the card, in light mode. */
  onLight: string;
  /** Same, dark mode. */
  onDark: string;
};

/**
 * The six US EPA bands.
 *
 * Two tones per band, because one cannot do both jobs: the vivid hue is legible as
 * a fill and illegible as text. Measured with the WCAG formula against the real card
 * surfaces (`elevation.level1`, #eef5f8 / #202529) and against a 14 % (light) / 20 %
 * (dark) tint of the fill over them — every `onLight` / `onDark` here clears 4.5:1 on
 * both. `AirQualityLegend` used to print white on every fill; on the yellow band that
 * measures 1.34:1.
 */
export const AQI_BANDS: AqiBand[] = [
  {
    key: 'good',
    low: 0,
    high: GOOD,
    fill: '#159868',
    ink: '#12160f',
    onLight: '#10724e',
    onDark: '#4db18c',
  },
  {
    key: 'moderate',
    low: GOOD + 1,
    high: MODERATE,
    fill: '#FEDD47',
    ink: '#12160f',
    onLight: '#7c6c23',
    onDark: '#FEDD47',
  },
  {
    key: 'sensitive',
    low: MODERATE + 1,
    high: SENSITIVE_UNHEALTHY,
    fill: '#FD9941',
    ink: '#12160f',
    onLight: '#955a26',
    onDark: '#FD9941',
  },
  {
    key: 'unhealthy',
    low: SENSITIVE_UNHEALTHY + 1,
    high: UNHEALTHY,
    fill: '#CA0A37',
    ink: '#ffffff',
    onLight: '#be0934',
    onDark: '#e06e89',
  },
  {
    key: 'veryUnhealthy',
    low: UNHEALTHY + 1,
    high: VERY_UNEAHTLY,
    fill: '#651297',
    ink: '#ffffff',
    onLight: '#651297',
    onDark: '#aa7dc6',
  },
  {
    key: 'hazardous',
    low: VERY_UNEAHTLY + 1,
    high: HAZARDOUS,
    fill: '#7D0425',
    ink: '#ffffff',
    onLight: '#7d0425',
    onDark: '#ba7a8b',
  },
];

export const aqiCategoryDescriptionList = [
  {
    message: messages.airQuality.good,
    description: messages.airQuality.goodDescription,
    value: GOOD,
    threshold: '0-50',
  },
  {
    message: messages.airQuality.moderate,
    description: messages.airQuality.moderateDescription,
    value: MODERATE,
    threshold: '51-100',
  },
  {
    message: messages.airQuality.sensitiveUnhealthy,
    description: messages.airQuality.sensitiveUnhealthyDescription,
    value: SENSITIVE_UNHEALTHY,
    threshold: '101-150',
  },
  {
    message: messages.airQuality.unhealthy,
    description: messages.airQuality.unhealthyDescription,
    value: UNHEALTHY,
    threshold: '151-200',
  },
  {
    message: messages.airQuality.veryUnhealthy,
    description: messages.airQuality.veryUnhealthyDescription,
    value: VERY_UNEAHTLY,
    threshold: '201-300',
  },
  {
    message: messages.airQuality.hazardous,
    description: messages.airQuality.hazardousDescription,
    value: HAZARDOUS,
    threshold: '300+',
  },
];

export const getAqiCategoryFromValue = (value: number) => {
  if (value <= GOOD) {
    return messages.airQuality.good;
  }
  if (value <= MODERATE) {
    return messages.airQuality.moderate;
  }
  if (value <= SENSITIVE_UNHEALTHY) {
    return messages.airQuality.sensitiveUnhealthy;
  }
  if (value <= UNHEALTHY) {
    return messages.airQuality.unhealthy;
  }
  if (value <= VERY_UNEAHTLY) {
    return messages.airQuality.veryUnhealthy;
  } else {
    return messages.airQuality.hazardous;
  }
};

/** The health sentence for a value's band — already translated in all 25 locales. */
export const getAqiDescriptionFromValue = (value: number) =>
  (
    aqiCategoryDescriptionList.find((item) => value <= item.value) ??
    aqiCategoryDescriptionList[aqiCategoryDescriptionList.length - 1]
  ).description;

export const getAqiColor = (value: number) => {
  if (value <= GOOD) {
    return '#159868';
  }
  if (value <= MODERATE) {
    return '#FEDD47';
  }
  if (value <= SENSITIVE_UNHEALTHY) {
    return '#FD9941';
  }
  if (value <= UNHEALTHY) {
    return '#CA0A37';
  }
  if (value <= VERY_UNEAHTLY) {
    return '#651297';
  } else {
    return '#7D0425';
  }
};

export const getAqiBandIndex = (value: number): number => {
  const index = AQI_BANDS.findIndex((band) => value <= band.high);
  return index === -1 ? AQI_BANDS.length - 1 : index;
};

export const getAqiBand = (value: number): AqiBand =>
  AQI_BANDS[getAqiBandIndex(value)];

/**
 * Position on the scale, 0..1, across six EQUAL segments.
 *
 * The six bands are not equal in AQI points — 0-50 against 300-500 — so this is a
 * category axis, not a value axis. Drawn proportionally every European reading sits
 * in the left fifth and the marker barely moves; the equal segments are deliberate.
 *
 * What is *not* deliberate is quantising: this interpolates inside the band, so 51
 * and 100 are 1/6th of the bar apart instead of identical.
 */
export const getAqiBandPosition = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  const index = getAqiBandIndex(value);
  const band = AQI_BANDS[index];
  const low = index === 0 ? 0 : band.low;
  const span = Math.max(band.high - low, 1);
  const withinBand = Math.min(Math.max((value - low) / span, 0), 1);
  return Math.min((index + withinBand) / AQI_BANDS.length, 1);
};

/** Foreground for text drawn on the solid band fill (legend chips, badges). */
export const getAqiInk = (value: number): string => getAqiBand(value).ink;

/** Foreground for text drawn on the card or on a tint of the band. */
export const getAqiOnColor = (value: number, dark: boolean): string =>
  dark ? getAqiBand(value).onDark : getAqiBand(value).onLight;

const TINT_LIGHT = 0.14;
const TINT_DARK = 0.2;

/**
 * Accepts `#rrggbb`, `#rgb` and `rgb()` / `rgba()`.
 *
 * MD3 stores the elevation levels as rgb strings — `elevation.level1` is
 * `'rgb(238, 245, 248)'` — while the AQI palette is hex. A hex-only parser
 * returns NaN for the surface, the mix comes out `#NaNNaNNaN`, and every tinted
 * element (chip, hero panel, day pill, sub-index badge) renders with no fill.
 * Invisible in a unit test that passes hex; obvious in one screenshot.
 */
const toRgb = (colour: string): [number, number, number] => {
  const rgbMatch = colour.match(/rgba?\(([^)]+)\)/);
  if (rgbMatch) {
    const parts = rgbMatch[1]
      .split(',')
      .map((part) => parseInt(part.trim(), 10));
    return [parts[0], parts[1], parts[2]];
  }
  const hex = colour.replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((character) => character + character)
          .join('')
      : hex;
  return [0, 2, 4].map((offset) =>
    parseInt(full.slice(offset, offset + 2), 16),
  ) as [number, number, number];
};

const mixHex = (
  foreground: string,
  background: string,
  amount: number,
): string => {
  const front = toRgb(foreground);
  const back = toRgb(background);
  const blended = [0, 1, 2].map((index) =>
    Math.round(front[index] * amount + back[index] * (1 - amount)),
  );
  return `#${blended.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/** The band fill composited into the surface it sits on — never the raw hue. */
export const getAqiTint = (
  value: number,
  surface: string,
  dark: boolean,
): string =>
  mixHex(getAqiBand(value).fill, surface, dark ? TINT_DARK : TINT_LIGHT);

/**
 * Ring fill for `AqiProgress`, 0..100.
 *
 * Was the band index / 6, which drew AQI 51 and AQI 100 identically. AqiProgress
 * still renders on the Davis AirLink and PM2.5 tiles — out of scope for the redesign,
 * so they get the continuous position rather than being left quantised.
 */
export const getAqiLevel = (value: number) => getAqiBandPosition(value) * 100;
